import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { dramas } from "@/mocks/dramas";
import { useFavorites } from "@/hooks/useFavorites";
import EpisodeList from "./components/EpisodeList";
import ClipPreview from "./components/ClipPreview";
import RelatedDramas from "./components/RelatedDramas";

export default function Detail() {
  const { id } = useParams();
  const drama = dramas.find((item) => item.id === id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    document.title = drama
      ? `${drama.title} - 剧集详情与剧情介绍`
      : "剧集不存在 - 迷人的保姆";
  }, [drama]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const related = useMemo(() => {
    if (!drama) return [];
    return dramas
      .filter((item) => item.id !== drama.id && item.categories.some((cat) => drama.categories.includes(cat)))
      .slice(0, 4);
  }, [drama]);

  if (!drama) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-20 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-movie-2-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-900">该剧集内容不存在</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的剧集可能已被移除或链接有误。</p>
        <Link
          to="/category"
          className="mt-5 flex items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回剧集分类
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(drama.id);

  function handleFavorite() {
    toggleFavorite(drama!.id);
    setToast(favorited ? "已取消收藏" : "收藏成功");
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 lg:px-8">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/category" className="transition-colors hover:text-primary-600">
          电视剧分类
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{drama.title}</span>
      </nav>

      <section className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-col gap-5 p-4 md:flex-row md:p-6">
          <div className="mx-auto h-64 w-44 shrink-0 overflow-hidden rounded-lg bg-background-200 md:mx-0 md:h-80 md:w-60">
            <img
              src={drama.cover}
              alt={`${drama.title} 高清封面`}
              title={`${drama.title} 剧集封面`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2">
              {drama.categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category?type=${encodeURIComponent(cat)}`}
                  className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-500 hover:text-background-50"
                >
                  {cat}
                </Link>
              ))}
              <span
                className={`rounded-full px-2.5 py-1 text-xs ${
                  drama.status === "更新中" ? "bg-accent-100 text-accent-800" : "bg-primary-100 text-primary-700"
                }`}
              >
                {drama.status}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{drama.title}</h1>
            <p className="mt-2 text-sm text-foreground-600">{drama.subtitle}</p>

            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
              <span className="flex items-center gap-1 text-accent-700">
                <i className="ri-star-fill"></i>
                <strong className="font-heading text-base">{drama.rating.toFixed(1)}</strong>
                <span className="text-xs text-foreground-500">/ 10</span>
              </span>
              <span className="flex items-center gap-1 text-foreground-600">
                <i className="ri-eye-line text-primary-500"></i>
                {drama.views} 播放
              </span>
              <span className="flex items-center gap-1 text-foreground-600">
                <i className="ri-calendar-line text-primary-500"></i>
                {drama.year} 年首播
              </span>
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">导演：</dt>
                <dd className="text-foreground-800">{drama.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">地区：</dt>
                <dd className="text-foreground-800">{drama.region}</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="shrink-0 text-foreground-500">主演：</dt>
                <dd className="text-foreground-800">{drama.cast.join(" / ")}</dd>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <dt className="shrink-0 text-foreground-500">更新：</dt>
                <dd className="text-foreground-800">
                  已更新 {drama.updatedEpisodes} / {drama.totalEpisodes} 集 · {drama.updateTime}
                </dd>
              </div>
            </dl>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-primary-950 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                {favorited ? "已收藏" : "收藏本剧"}
              </button>
              <a
                href="#episodes"
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-list-ordered"></i>
                查看分集剧情
              </a>
            </div>
            <p className="mt-3 text-[11px] text-foreground-400">
              <i className="ri-information-line"></i> 收藏功能基于本机浏览器存储，仅作演示，无需登录。
            </p>
          </div>
        </div>
      </section>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="flex flex-col gap-5 lg:col-span-2">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
              <i className="ri-book-open-line text-primary-500"></i>
              剧集简介
            </h2>
            <p className="text-sm leading-relaxed text-foreground-700">{drama.synopsis}</p>
            <h3 className="mb-2 mt-5 flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-lightbulb-line text-accent-600"></i>
              剧情深度解析
            </h3>
            <p className="text-sm leading-relaxed text-foreground-700">{drama.analysis}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {drama.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                  #{tag}
                </span>
              ))}
            </div>
          </section>

          <EpisodeList dramaId={drama.id} episodes={drama.episodes} />
          <ClipPreview clips={drama.clips} />
        </div>

        <aside className="flex flex-col gap-5 lg:col-span-1">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <h2 className="mb-3 flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-team-line text-primary-500"></i>
              主要角色
            </h2>
            <ul className="flex flex-col gap-3">
              {drama.roles.map((role) => (
                <li key={role.name} className="flex gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-100 font-heading text-sm font-semibold text-accent-800">
                    {role.name.slice(0, 1)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900">
                      {role.name}
                      <span className="ml-1 text-xs font-normal text-foreground-500">饰 {role.actor}</span>
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-foreground-600">{role.desc}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <RelatedDramas dramas={related} />

          <section className="rounded-lg border border-primary-200 bg-primary-50 p-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-primary-800">
              <i className="ri-bookmark-3-line"></i>
              所属分类
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {drama.categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category?type=${encodeURIComponent(cat)}`}
                  className="cursor-pointer rounded-md bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:text-primary-600"
                >
                  {cat}
                </Link>
              ))}
            </div>
          </section>
        </aside>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full border border-foreground-800 bg-foreground-950 px-5 py-2.5 text-sm text-background-50">
          {toast}
        </div>
      )}
    </div>
  );
}